import React from 'react'
import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/factorial-one-react/dist/experimental'
import { NodePageDocument, type NodePageQuery } from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

import { NodeTabs } from 'modules/job_catalog/components/NodeTabs'
import {
  NODE_TYPES,
  WorkingConditionItem,
  formatConditionName,
  formatUuidToNodeType,
  formatWorkingConditionValue,
  getChildNodeType,
  getTextFromNodeType,
  isNodeType,
  nodeTypeIcon,
} from 'modules/job_catalog/lib/nodesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface ParsedWorkingCondition {
  items: WorkingConditionItem[]
  working_conditions_group_name: string
}

const WorkingConditionsPage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = levelId || roleId || functionId || familyId || rootId || ''
  const childType = getChildNodeType(id)

  const [totalWorkingConditions, setTotalWorkingConditions] = React.useState(0)

  const source = useGraphqlDataSource({
    document: NodePageDocument,
    mapData: (data: NodePageQuery) => {
      const attributes = data?.jobCatalog?.nodeAttributesConnection?.nodes ?? []
      const items = attributes
        .filter((attr: { type: string }) => attr.type === 'WorkingConditions')
        .filter((attr) => attr.takesPrecedence)
        .flatMap((attr: { serializedValue: string; definedAt: string }) => {
          const parsed: ParsedWorkingCondition = JSON.parse(attr.serializedValue || '{}')
          const type = isNodeType(attr.definedAt) ? attr.definedAt : NODE_TYPES.ROOT
          setTotalWorkingConditions(parsed.items.length)

          return parsed.items.map((item: WorkingConditionItem) => ({
            groupName: parsed.working_conditions_group_name,
            conditionName: formatConditionName(item.name),
            value: formatWorkingConditionValue(item),
            icon: nodeTypeIcon(type),
            definedAt: getTextFromNodeType(type),
          }))
        })

      return items.sort((a: { groupName: string }, b: { groupName: string }) =>
        a.groupName.localeCompare(b.groupName)
      )
    },
    mapPagination: () => ({
      total: totalWorkingConditions,
    }),
    mapVariables: () => ({
      nodeId: Number(id?.split('-')[1]),
      nodeType: formatUuidToNodeType(id || ''),
      childType: formatUuidToNodeType(childType || ''),
    }),
  })

  return (
    <ResponsivePage.Body variant="full">
      <NodeTabs />
      <Box paddingTop="s20">
        <Text.Header3>
          {totalWorkingConditions} {i18n.t('job_catalog.nodes.work_conditions')}
        </Text.Header3>
      </Box>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('job_catalog.nodes.condition'),
                  render: (item) => ({
                    type: 'text',
                    value: item.conditionName,
                  }),
                },
                {
                  label: i18n.t('job_catalog.nodes.value'),
                  render: (item) => ({
                    type: 'text',
                    value: item.value,
                  }),
                },
                {
                  label: '',
                  render: (item) => ({
                    type: 'tag',
                    value: { label: item.definedAt, icon: item.icon },
                  }),
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
    </ResponsivePage.Body>
  )
}

export default WorkingConditionsPage
